import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('.cart__checkout-button') : null;
      if (!button || button.hasAttribute('aria-busy')) return;
      button.setAttribute('aria-busy', 'true');
    });
    this.addEventListener('click', this.#onEmptyLinkClick);
  }

  /**
   * Continue shopping links to the homepage buy box. On the homepage itself the jump waits until the drawer
   * has closed, because its scroll lock swallows the jump while the drawer is still open.
   * @param {MouseEvent} event
   */
  #onEmptyLinkClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a.cart-items__empty-button') : null;
    if (!(link instanceof HTMLAnchorElement) || !link.hash) return;
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname) return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;

    event.preventDefault();
    this.addEventListener(
      'dialog:close',
      () => {
        requestAnimationFrame(() => {
          history.replaceState(null, '', url.hash);
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      },
      { once: true }
    );
    this.closeDialog();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  beginUpdate() {
    this.setAttribute('updating', '');
  }

  endUpdate() {
    this.removeAttribute('updating');
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/** @returns {CartDrawerComponent[]} */
export function cartDrawers() {
  return /** @type {CartDrawerComponent[]} */ ([...document.querySelectorAll('cart-drawer-component')]);
}

export function beginCartUpdate() {
  cartDrawers().forEach((drawer) => drawer.beginUpdate());
}

export function endCartUpdate() {
  cartDrawers().forEach((drawer) => drawer.endUpdate());
}
